import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    type MapboxMap,
    TrackedRideContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { TrackedRide } from '@polaris-customerweb/sharedcomponents.user-content';
import { Feature, LineString, MultiLineString } from 'geojson';
import { includes } from 'lodash-es';
import type {
    PointLike,
    SourceSpecification,
    SymbolLayerSpecification,
} from 'mapbox-gl';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { TrackedRideService } from './tracked-ride.service';

@Injectable({ providedIn: 'root' })
export class TrackedRideMapService extends MapContentMapService {
    override minZoom = 8;
    sourceIdPoints = 'trackedRidePoints';
    layerIdName = 'trackedRidesName';
    layerIdSelectedName = 'trackedRidesSelectedName';

    private emptySource: SourceSpecification = {
        data: new GeoJsonFeatureCollection([]),
        type: 'geojson',
    };

    private rideNameLayer: SymbolLayerSpecification;
    private selectedNameLayer: SymbolLayerSpecification;

    constructor(
        private configService: ConfigService,
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private trackedRideService: TrackedRideService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('trackedrides');

        this.rideNameLayer = {
            id: this.layerIdName,
            layout: {
                'symbol-placement': 'line',
                'text-anchor': 'bottom',
                'text-field': '{name}',
                'text-letter-spacing': 0.1,
                'text-offset': [0, -0.2],
                'text-size': 10,
            },
            minzoom: this.minZoom,
            paint: {
                'text-color': '#3b3b3b',
                'text-halo-color': '#ffffff',
                'text-halo-width': 0.75,
            },
            source: this.sourceId,
            type: 'symbol',
        };

        this.selectedNameLayer = {
            filter: [
                'all',
                true,
                ['match', ['get', 'id'], ['SELECTED_ID'], true, false],
            ],
            id: this.layerIdSelectedName,
            layout: {
                'symbol-placement': 'line',
                'text-anchor': 'bottom',
                'text-field': '{name}',
                'text-letter-spacing': 0.1,
                'text-offset': [0, -0.2],
                'text-size': 10,
            },
            minzoom: this.minZoom,
            paint: {
                'text-color': '#3b3b3b',
                'text-halo-color': '#ffffff',
                'text-halo-width': 0.75,
            },
            source: this.sourceId,
            type: 'symbol',
        };
    }

    override addSources(mapboxMap: MapboxMap) {
        super.addSources(mapboxMap);

        this.mapService.addSource(
            mapboxMap,
            this.sourceIdPoints,
            this.emptySource
        );

        this.layerIds
            .filter((layerId) =>
                includes(layerId.toLowerCase(), 'selectedstart')
            )
            .forEach((layerId) => {
                this.mapService.replaceLayer(
                    mapboxMap,
                    layerId,
                    this.sourceIdPoints
                );
            });
        this.mapService.insertLayer(mapboxMap, this.rideNameLayer);
        this.mapService.insertLayer(mapboxMap, this.selectedNameLayer);

        this.layerIdsUnselected.push(this.layerIdName);
        this.layerIdsSelected.push(this.layerIdSelectedName);
    }

    setSourceDataFromModel(mapboxMap: MapboxMap, trackedRides: TrackedRide[]) {
        const geoJson =
            trackedRides.length > 0
                ? this.trackedRideService.trackedRidesToGeoJson(trackedRides)
                : undefined;
        const geoJsonPoints =
            this.trackedRideService.trackedRidesToGeoJsonPoints(trackedRides);

        super.setSourceData(mapboxMap, geoJson);

        this.mapService.setSourceData(
            mapboxMap,
            this.sourceIdPoints,
            geoJsonPoints
        );
    }

    getRideAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): TrackedRide | undefined {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        const rideLayerData = features[0];

        if (features.length === 0) {
            return undefined;
        }

        const ride = this.trackedRideService.getCachedTrackedRide(
            rideLayerData.properties?.id as string
        );

        if (ride) {
            return ride;
        }

        // Mapbox appears to convert LineString object to MultiLineString when it intersects itself.
        // TrackedRide constructor requires a LineString so convert to one.
        // We don't expect to ever hit this line anyway, as the ride should be cached in the ride service.
        if (rideLayerData.geometry.type === 'MultiLineString') {
            const trackedRideData = this.convertMultiLineStringToLineString(
                rideLayerData as unknown as Feature<MultiLineString>
            );
            return new TrackedRide(
                trackedRideData as TrackedRideContract,
                this.configService.appSku
            );
        }

        return new TrackedRide(
            rideLayerData as TrackedRideContract,
            this.configService.appSku
        );
    }

    private convertMultiLineStringToLineString(
        multiLineString: Feature<MultiLineString>
    ): Feature<LineString> {
        const longestPath = multiLineString.geometry.coordinates.reduce(
            (result, lineString) => {
                return lineString.length > result.length ? lineString : result;
            },
            []
        );

        return {
            geometry: {
                coordinates: [...longestPath],
                type: 'LineString',
            },
            properties: { ...multiLineString.properties },
            type: 'Feature',
        };
    }
}
